import {
  AnimalScreeningReportVendors,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { ApplicantOrInvitee, Verification } from "../../types";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import { ReportStatusBadge } from "../ReportStatusBadge";
import { useQuery } from "@tanstack/react-query";
import { APPROVED_PET_STATUSES } from "views/applications-id-report/slices/PetScreeningReportSlice/constants";
import { useMemo } from "react";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import api from "api/landlord/applications";
import {
  getAnimalScreeningRawResponseAnimals,
  getVerificationStatusByVendor,
  hasCompletedAnimalScreeningReport
} from "views/applications-id-report/slices/PetScreeningReportSlice/utils";
import { VerificationReport } from "types/landlord/reports";
import { PetScreeningReport } from "views/applications-id-report/slices/PetScreeningReportSlice/types";
import { AxiosError } from "axios";
import { useAuthContext } from "providers/Auth";

interface PetScreeningReportSectionProps {
  verification: Verification;
  applicant: ApplicantOrInvitee;
}

export const PetScreeningReportSection = ({
  verification,
  applicant
}: PetScreeningReportSectionProps) => {
  const { permissions } = useAuthContext();
  const { id: verificationId } = verification;

  const query = useQuery<VerificationReport<PetScreeningReport>, AxiosError>({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.PetScreeningReport,
      applicationId: applicant.application_id || 0,
      applicantId: applicant.id || 0,
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id || 0,
          applicant.id || 0,
          verificationId
        )
        .then((data) => data.verification)
  });

  const { data: verificationData } = query;
  const {
    report,
    requested_at: emailSentAt,
    status,
    vendor
  } = verificationData || {};
  const pets =
    report?.pets ?? getAnimalScreeningRawResponseAnimals(verificationData);
  const numPets = pets?.length || 0;
  const hasCompletedReport =
    hasCompletedAnimalScreeningReport(verificationData);

  const numVerifiedPets =
    (vendor &&
      numPets &&
      pets?.filter((pet) =>
        APPROVED_PET_STATUSES.includes(
          getVerificationStatusByVendor(
            pet,
            vendor as AnimalScreeningReportVendors
          )
        )
      ).length) ||
    0;

  const { application_status } = applicant;

  const getVerificationStatus = (): VerificationStatus => {
    if (
      hasCompletedReport ||
      numVerifiedPets ||
      status === VerificationStatus.Completed
    )
      return VerificationStatus.Finished;

    if (emailSentAt && verification.status === VerificationStatus.Pending)
      return VerificationStatus.EmailSent;

    return VerificationStatus.Processing;
  };

  const numPetsText = useMemo(() => {
    if (!report) return "-";
    if (!numPets) return "0";
    return `${numVerifiedPets} of ${numPets}`;
  }, [report, numPets, numVerifiedPets]);

  return (
    <Cell
      isDataCell
      className="relative"
      isPermitted={permissions.canAccessReports.animalScreening}
      showRestrictedFlag={!permissions.canAccessReports.animalScreening}
    >
      {numPetsText}
      <ReportStatusBadge
        applicantStatus={application_status}
        verificationStatus={getVerificationStatus()}
        executionMode={verification.settings?.execution_mode}
      />
    </Cell>
  );
};
